import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, brl, formatApiError } from "@/lib/api";

const STATUS_META = {
  pendente: { label: "Pendente Pix", cls: "bg-amber-100 text-amber-800" },
  producao: { label: "Em produção", cls: "bg-blue-100 text-blue-800" },
  concluido: { label: "Concluído", cls: "bg-emerald-100 text-emerald-800" },
  cancelado: { label: "Cancelado", cls: "bg-neutral-200 text-neutral-700" },
};

export default function OrdersTab() {
  const [orders, setOrders] = useState(null);

  const load = () =>
    api
      .get("/admin/orders")
      .then((r) => setOrders(r.data))
      .catch((e) => {
        if (e?.response?.status === 401) window.location.reload();
        else toast.error(formatApiError(e));
      });

  useEffect(() => {
    load();
  }, []);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}`, { status });
      toast.success("Status atualizado.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (orders === null) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-ink-3" data-testid="orders-loading" />
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <p className="py-16 text-center text-sm text-ink-2" data-testid="orders-empty">
        Nenhum pedido recebido ainda.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="orders-list">
      {orders.map((o) => {
        const meta = STATUS_META[o.status] || STATUS_META.pendente;
        const waDigits = (o.client_whatsapp || "").replace(/\D/g, "");
        return (
          <div key={o.id} className="rounded-2xl border border-line bg-white p-6" data-testid={`admin-order-card-${o.code}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs font-bold tracking-widest">{o.code}</span>
                <span className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${meta.cls}`} data-testid={`admin-order-status-${o.code}`}>
                  {meta.label}
                </span>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-ink-3">
                {new Date(o.created_at).toLocaleDateString("pt-BR")} ·{" "}
                {new Date(o.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
              </span>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <p className="font-display text-lg font-bold">{o.client_name}</p>
                <p className="mt-1 text-sm text-ink-2">{o.service_title} · {brl(o.service_price)}</p>
                <p className="mt-1 text-sm text-ink-2">{o.client_email}</p>
                <div className="mt-3 flex gap-3">
                  {waDigits && (
                    <a
                      href={`https://wa.me/${waDigits}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`admin-order-whatsapp-${o.code}`}
                      className="font-mono text-[10px] uppercase tracking-widest text-accent underline"
                    >
                      WhatsApp {o.client_whatsapp}
                    </a>
                  )}
                </div>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-ink-3">Briefing</p>
                <p className="mt-1 text-sm leading-relaxed text-ink-2">{o.brief}</p>
              </div>
            </div>
            <div className="mt-4 border-t border-line pt-4">
              <label className="mr-3 font-mono text-[10px] uppercase tracking-widest text-ink-3">Status</label>
              <select
                data-testid={`admin-order-status-select-${o.code}`}
                value={o.status}
                onChange={(e) => changeStatus(o.id, e.target.value)}
                className="rounded-lg border border-input bg-white px-3 py-2 text-sm"
              >
                {Object.entries(STATUS_META).map(([value, m]) => (
                  <option key={value} value={value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );
      })}
    </div>
  );
}
